import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import { createStructuredSelector } from 'reselect';

import DefaultAvatar from 'atoms/DefaultAvatar';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import AttendanceRecord from 'components/AttendanceRecord/index';
import PatientCareGroup from 'components/PatientCareGroup';
import PatientTherapies from 'components/PatientTherapies';
import PatientTherapiesHistory from 'components/PatientTherapiesHistory';
import { makeSelectIsMobile } from 'containers/App/selectors';
import { CURRENT, FORMER, rowsForEnrollmentList } from 'utils/enrollments';

import {
	fetchPatientProfile,
	fetchPatientCalendar,
	fetchPatientGroups,
	fetchPatientOq,
} from './actions';
import {
	makeSelectProfile,
	makeSelectProfileLoading,
	makeSelectCalendarEvents,
	makeSelectCalendarLoading,
	makeSelectGroups,
	makeSelectGroupsLoading,
	makeSelectOq,
	makeSelectOqLoading,
} from './selectors';

export function ReferrerPatientView({
	dispatch,
	match,
	profile,
	profileLoading,
	calendarEvents,
	calendarLoading,
	groups,
	groupsLoading,
	oq,
	oqLoading,
}) {
	const [demoTab, setDemoTab] = useState('demographic');
	const [detailsTab, setDetailsTab] = useState('attendance');
	const [showSubscoreChart, setShowSubscoreChart] = useState(false);

	const patientId = match && match.params ? parseInt(match.params.patientId, 10) : null;

	useEffect(() => {
		if (patientId) {
			dispatch(fetchPatientProfile(patientId));
			dispatch(fetchPatientCalendar(patientId));
			dispatch(fetchPatientGroups(patientId));
		}
	}, [patientId]);

	const loadTab = useCallback((tab) => {
		if (!patientId) return;
		switch (tab) {
			case 'attendance':
				dispatch(fetchPatientCalendar(patientId));
				break;
			case 'oq':
				dispatch(fetchPatientOq(patientId));
				break;
			default:
				break;
		}
	}, [patientId, dispatch]);

	const handleDetailsTab = (tab) => {
		setDetailsTab(tab);
		loadTab(tab);
	};

	// `/referrer/patients/:id/groups` runs the same aggregating query as
	// `/patient/:id/programs`: one row per group, every instance in `enrollments`.
	// Each list shows at most one row per group — the instance that put it there — so
	// a group the patient left and rejoined appears in both, dated differently in
	// each. PatientTherapies/PatientTherapiesHistory are shared with PatientProfile
	// and read flat fields, so flatten the displayed instance back onto the row.
	const therapyRows = (listType) =>
		rowsForEnrollmentList(groups, listType).map(({ enrollment, ...group }) => ({
			...group,
			ptpId: enrollment.id,
			startDate: enrollment.startDate,
			endDate: enrollment.endDate || null,
		}));

	const enrolledGroups = therapyRows(CURRENT).sort((a, b) =>
		(a.groupName || '').localeCompare(b.groupName || ''),
	);

	const unenrolledGroups = therapyRows(FORMER).sort((a, b) =>
		moment(b.endDate).diff(moment(a.endDate)),
	);

	const demoTabs = [
		{ id: 'demographic', label: 'Demographic' },
		{ id: 'careProviders', label: 'Care Providers' },
	];

	const detailsTabs = [
		{ id: 'attendance', label: 'Attendance Calendar' },
		{ id: 'enrolledTherapies', label: 'Enrolled Therapies' },
		{ id: 'unenrolledTherapies', label: 'Unenrolled Therapies' },
		{ id: 'oq', label: 'Treatment Progress' },
	];

	const renderDemographicRow = (label, value) => (
		<div className="row w-100" key={label}>
			<div className="col p-2 align-top font-13" style={{ width: '45%' }}>
				{label}
			</div>
			<div className="col p-2 font-13">{value || '-'}</div>
		</div>
	);

	const renderDemographic = () => {
		if (!profile) return <p className="text-muted p-3">No profile data available.</p>;
		return (
			<div className="d-flex bg-white p-4 w-100">
				<div className="w-100">
					{renderDemographicRow('Name', profile.name)}
					{renderDemographicRow('Date of Birth', profile.dob ? moment(profile.dob).format('MM/DD/YYYY') : null)}
					{renderDemographicRow('Age', profile.age)}
					{renderDemographicRow('Gender', profile.gender)}
					{renderDemographicRow('Registration Date', profile.registrationDate ? moment(profile.registrationDate).format('MM/DD/YYYY') : null)}
					{renderDemographicRow('Start Date', profile.startDate ? moment(profile.startDate).format('MM/DD/YYYY') : null)}
					{renderDemographicRow('Discharge Date', profile.dischargeDate ? moment(profile.dischargeDate).format('MM/DD/YYYY') : null)}
				</div>
			</div>
		);
	};

	const renderCareProviders = () => {
		if (!profile) return <p className="text-muted p-3">No data available.</p>;
		const cg = profile.careGroup || {};
		return (
			<div className="row">
				<div className="col-sm-12">
					<PatientCareGroup
						data={{ md: cg.md || {}, therapist: cg.therapist || {}, mtc: cg.mtc || [], outside: cg.outside || [] }}
						mainClinician={profile.mainClinician || {}}
					/>
				</div>
			</div>
		);
	};

	const renderOq = () => {
		if (oqLoading) return <SmallLoadingIndicator />;
		if (!oq || !oq.chartUrl) return <p className="text-muted">No OQ data available.</p>;
		return (
			<div>
				<div className="d-flex align-items-center mb-2">
					<h6 className="m-0 mr-3">{showSubscoreChart ? 'Subscores' : 'Total Score'}</h6>
					{oq.subscoreChartUrl && (
						<button
							className="btn btn-sm btn-outline-secondary font-10"
							onClick={() => setShowSubscoreChart(!showSubscoreChart)}
						>
							{showSubscoreChart ? 'Show Total Score' : 'Show Subscores'}
						</button>
					)}
				</div>
				<img
					src={showSubscoreChart ? oq.subscoreChartUrl : oq.chartUrl}
					alt={showSubscoreChart ? 'OQ Subscore Chart' : 'OQ Total Score Chart'}
					className="img-fluid"
				/>
			</div>
		);
	};

	return (
		<>
			{/* Header banner */}
			<div className="container-fluid bg-white px-5 py-3">
				<div className="row py-2 bg-white p-0">
					<div className="w-100 d-flex justify-content-center">
						{profileLoading ? (
							<SmallLoadingIndicator />
						) : profile ? (
							<>
								<div className="mr-3">
									<DefaultAvatar name={profile.name} width={60} />
								</div>
								<div className="h-100">
									<h5 className="text-dark mb-1 font-weight-bold">
										{profile.name}
									</h5>
									{profile.levelOfCare && (
										<div className="font-12 text-muted mb-2 pb-2 border-bottom">
											{profile.levelOfCare}
										</div>
									)}
									{!profile.levelOfCare && <div className="mb-2 pb-2 border-bottom" />}
								</div>
							</>
						) : (
							<p className="text-muted">Patient not found or no active authorization.</p>
						)}
					</div>
				</div>
			</div>

			{/* Main content */}
			<div className="container-fluid my-2 px-5">
				<div className="row">
					<div className="col-auto text-uppercase font-weight-bold font-11 py-2 mb-1">
						<Link to="/referrer/dashboard" className="text-decoration-none text-dark">
							&larr; Back to Patients
						</Link>
					</div>
				</div>
				<div className="row justify-content-between pb-4">
					{/* Left panel — Demographics */}
					<div
						className="col-auto p-0 mr-5"
						style={{ width: 484 }}
					>
						<Nav
							tabs
							className="actions m-0 mb-2 border-bottom-0 shadow-none"
							style={{ position: 'relative' }}
						>
							{demoTabs.map((tab) => (
								<NavItem key={tab.id}>
									<NavLink
										className={classnames(
											{ active: demoTab === tab.id },
											'font-13 hand',
										)}
										onClick={() => setDemoTab(tab.id)}
									>
										{tab.label}
									</NavLink>
								</NavItem>
							))}
						</Nav>
						<TabContent activeTab={demoTab}>
							<TabPane tabId="demographic">
								<div className="row">
									<div className="col-sm-12">
										{profileLoading ? <SmallLoadingIndicator /> : renderDemographic()}
									</div>
								</div>
							</TabPane>
							<TabPane tabId="careProviders">
								{profileLoading ? <SmallLoadingIndicator /> : renderCareProviders()}
							</TabPane>
						</TabContent>
					</div>

					{/* Right panel — Details */}
					<div className="col p-0" style={{ minWidth: '480px' }}>
						<Nav
							tabs
							className="actions m-0 mb-2 border-bottom-0 shadow-none"
							style={{
								display: 'block',
								overflow: 'auto hidden',
								whiteSpace: 'nowrap',
							}}
						>
							{detailsTabs.map((tab) => (
								<NavItem key={tab.id} style={{ display: 'inline-block' }}>
									<NavLink
										className={classnames(
											{ active: detailsTab === tab.id },
											'font-13 hand',
										)}
										onClick={() => handleDetailsTab(tab.id)}
									>
										{tab.label}
									</NavLink>
								</NavItem>
							))}
						</Nav>
						<TabContent activeTab={detailsTab}>
							<TabPane tabId="attendance">
								<div className="row">
									<div className="col-sm-12">
										{calendarLoading ? (
											<SmallLoadingIndicator />
										) : (
											<AttendanceRecord
												events={calendarEvents}
												type="referrer"
											/>
										)}
									</div>
								</div>
							</TabPane>

							<TabPane tabId="enrolledTherapies">
								<div className="row">
									<div className="col-sm-12">
										<PatientTherapies
											data={enrolledGroups}
											type="referrer"
											loading={groupsLoading}
										/>
									</div>
								</div>
							</TabPane>

							<TabPane tabId="unenrolledTherapies">
								<div className="row">
									<div className="col-sm-12">
										<PatientTherapiesHistory
											data={unenrolledGroups}
											type="referrer"
											loading={groupsLoading}
										/>
									</div>
								</div>
							</TabPane>

							<TabPane tabId="oq">
								<div className="row">
									<div className="col-sm-12">
										{renderOq()}
									</div>
								</div>
							</TabPane>
						</TabContent>
					</div>
				</div>
			</div>
		</>
	);
}

const mapStateToProps = createStructuredSelector({
	profile: makeSelectProfile(),
	profileLoading: makeSelectProfileLoading(),
	calendarEvents: makeSelectCalendarEvents(),
	calendarLoading: makeSelectCalendarLoading(),
	groups: makeSelectGroups(),
	groupsLoading: makeSelectGroupsLoading(),
	oq: makeSelectOq(),
	oqLoading: makeSelectOqLoading(),
	isMobile: makeSelectIsMobile(),
});

export default connect(mapStateToProps)(ReferrerPatientView);
